
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {FILL_COLOR, FILL_OPACITY} from "./areaTool.js";
import {sampleCirclePolygon} from "../geometry/circleFromThreePoints.js";
import {CURVE_SAMPLING_TOLERANCE_M} from "../geometry/arcFromSagitta.js";
import {createDialogOverlay, fillDialog, pvpButton} from "../DialogHelpers.js";
import {toVector3Array, toPlainPoint} from "./shapeCaptureHelpers.js";
import {createMarker} from "./perpendicularCapture.js";
import {captureFixedPoints} from "./pipeTool.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {createLivePlaneEstimator} from "./livePlaneEstimator.js";
import {projectOntoPlane} from "../geometry/localPlaneFit.js";
import {distanceBetween} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";
import {formatArea, averagePosition} from "./areaTool.js";

import {t, T} from "../i18n/index.js";
const PREVIEW_COLOR = 0xffdc00; // matches every other construction-preview in this codebase
const WORLD_UP = {x: 0, y: 0, z: 1};

// "Cirkel". Capture is click-center -> dynamic radius (mouse distance to
// the center, in the currently estimated plane) -> click to confirm - the
// surrounding point cloud is queried continuously (throttled, via
// tools/livePlaneEstimator.js) to estimate the circle's own local 3D plane
// while the radius is being dragged, so the operator only ever places the
// centre and picks a radius; the plane comes from the visible surface, not
// a third manually-clicked periphery point. The point cloud only ORIENTS
// the circle (geometry/localPlaneFit.js's projectOntoPlane moves the
// centre along the plane's normal only) - it never deforms the ring of
// vertices itself, which is always regenerated as an exact circle
// (sampleCirclePolygon) from centre+radius+plane. The underlying registered
// feature is an ordinary Polygon (task: "Den underliggende GIS-feature skal
// fortsat være almindelig polygongeometri") - only `feature.domainData.circle`
// remembers that it is, parametrically, a circle (see
// geometry/parametricShapeStore.js for the shared whole-feature-shape
// invalidation rule this relies on).
export const CircleTool = {
	id: "digitize_circle",
	name: T("Cirkel"),
	category: "basic",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 40,
	icon: "digitize_circle.svg",
	resultGeometryType: "Polygon",
	tooltip: T("Digitalisér en cirkel: klik for centrum, flyt musen for radius - punktskyen estimerer automatisk det lokale plan."),
	instructions: {
		center: T("Venstreklik for at placere cirklens centrum."),
		radius: T("Flyt musen for at angive radius - punktskyen estimerer automatisk det lokale plan. Venstreklik for at afslutte."),
	},
	fillColor: FILL_COLOR,
	fillOpacity: FILL_OPACITY,
	customCapture(dm, tool){ runCircleCapture(dm, tool); },
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished circle keeps its result label.
	decorateLoaded(visual){ enableCircleResultLabel(visual); },
};

DigitizationTools.register(CircleTool);

// The finished circle's result label (name, radius, area): a pure function of the periphery points, shared by the capture flow and the
// restore hook so both produce the same label from the same geometry. sampleCirclePolygon() places the points at equal angular
// steps around the FULL circle, so their average is exactly the centre.
export function circleSummaryLines(raw, v){
	if(raw.length < 3) return null;
	const center = averagePosition(raw);
	const radius = distanceBetween([center.x, center.y, center.z], raw[0]);
	return [
		v.name,
		`Radius: ${formatLength(radius)}`,
		t("Areal: {0}", formatArea(Math.PI * radius * radius)),
	];
}

export function enableCircleResultLabel(visual){
	visual.enableSummaryLabel(circleSummaryLines, (raw) => averagePosition(raw));
}

function buildCircleShape(fit){
	return {
		type: "circle",
		center: [fit.center.x, fit.center.y, fit.center.z],
		radiusM: fit.radiusM,
		u: [fit.u.x, fit.u.y, fit.u.z],
		v: [fit.v.x, fit.v.y, fit.v.z],
	};
}

function planeFromShape(shape){
	return {
		center: {x: shape.center[0], y: shape.center[1], z: shape.center[2]},
		u: {x: shape.u[0], y: shape.u[1], z: shape.u[2]},
		v: {x: shape.v[0], y: shape.v[1], z: shape.v[2]},
	};
}

// Live-preview dialog shared by "Cirkel" (brand-new, `existingShape` omitted)
// and "Rediger cirkel…" (opened with the stored centre/plane/radius,
// `existingShape` given) - task DEL A/B, same architecture as
// VectorEditingMenu.js's openArcDialog(): visual.setPoints() drives the
// live preview directly, re-asserting feature.domainData.circle after every
// one of its own edits (see geometry/parametricShapeStore.js's own doc
// comment for why that is what stops the generic invalidation rule from
// dissolving the very shape being edited), Annuller/ESC restores the exact
// original geometry+metadata snapshotted at open time. Vertex count is
// dynamic (same CURVE_SAMPLING_TOLERANCE_M chord-error budget arcs use -
// task DEL B: "Genbrug samme princip som de eksisterende parametriske
// buer"), radius and diameter fields are kept synchronised (two views of
// the one radiusM parameter) - centre and plane never change here (task:
// "Center og plan bevares under denne numeriske justering"). The 3D plane
// found during capture (or restored from the stored shape, for "Rediger
// cirkel…") is kept as-is here too - only radiusM changes.
//
// Displayed/entered in CENTIMETER (task: "Alle brugerindtastede
// dimensionsværdier skal være i centimeter") - PointView's own internal
// geometry (radiusM) stays in METER throughout; the ×100/÷100 conversion
// happens only at this UI boundary, right where the value is read from or
// written to the DOM.
function openCircleDialog(dm, visual, {center, u, v}, initialRadiusM, {isNewFeature} = {}){
	return new Promise((resolve) => {
		const feature = dm.getFeature(visual);
		if(!feature){ resolve(false); return; }

		const originalPoints = visual.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));
		const originalShape = feature.domainData.circle ? {...feature.domainData.circle} : null;

		let radiusM = initialRadiusM;
		let done = false;

		const applyPreview = () => {
			const sample = sampleCirclePolygon(center, radiusM, {u, v}, CURVE_SAMPLING_TOLERANCE_M);
			visual.setPoints(toVector3Array(sample.points));
			const shape = buildCircleShape({center, u, v, radiusM});
			dm.featureManager.setDomainData(feature.id, "circle", shape);
			return sample;
		};

		const {overlay, box} = createDialogOverlay(dm, 280);
		fillDialog(box, {
			title: `${isNewFeature ? t("Cirkel") : t("Rediger cirkel")}`,
			description: t(`Radius og diameter er synkroniserede. Ændringen vises direkte i 3D, mens du skriver. Center og plan bevares.`),
			body: `<label class="pvp-label">${t("Radius [cm]")}</label>
				<input class="pvp-input" type="number" step="any" min="0" value="${(radiusM * 100).toFixed(1)}" data-role="radius"
				 />
				<label class="pvp-label">${t("Diameter [cm]")}</label>
				<input class="pvp-input" type="number" step="any" min="0" value="${(radiusM * 2 * 100).toFixed(1)}" data-role="diameter"
				 />
				<div class="pvp-note" data-role="info"></div>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('commit', t("Godkend"), 'primary'),
		});

		const radiusInput = box.querySelector('[data-role="radius"]');
		const diameterInput = box.querySelector('[data-role="diameter"]');
		const infoEl = box.querySelector('[data-role="info"]');
		radiusInput.focus();
		radiusInput.select();

		const updateInfo = (sample) => {
			infoEl.textContent = t("{0} segmenter", sample.segmentCount);
		};
		updateInfo(applyPreview());

		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};

		const finish = (committed) => {
			if(done) return;
			done = true;
			cleanup();
			if(committed){
				dm.clearMultiSelection();
			}else{
				visual.setPoints(toVector3Array(originalPoints));
				if(originalShape) dm.featureManager.setDomainData(feature.id, "circle", originalShape);
				else dm.featureManager.clearDomainData(feature.id, "circle");
			}
			resolve(committed);
		};

		radiusInput.addEventListener('input', () => {
			const parsedCm = parseFloat(radiusInput.value);
			if(isFinite(parsedCm) && parsedCm > 0){
				radiusM = parsedCm / 100;
				diameterInput.value = (radiusM * 2 * 100).toFixed(1);
				updateInfo(applyPreview());
			}
		});
		diameterInput.addEventListener('input', () => {
			const parsedCm = parseFloat(diameterInput.value);
			if(isFinite(parsedCm) && parsedCm > 0){
				radiusM = (parsedCm / 100) / 2;
				radiusInput.value = (radiusM * 100).toFixed(1);
				updateInfo(applyPreview());
			}
		});
		box.querySelector('[data-action="commit"]').addEventListener('click', () => finish(true));
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(false));
		const onCancelEvent = () => finish(false);
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(false); }
			if(e.key === 'Enter'){ e.preventDefault(); finish(true); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

function runCircleCapture(dm, tool){
	// Phase 1: a single click places the centre (reuses pipeTool.js's
	// generic "N fixed left-clicks" capture - the same primitive
	// shapeCaptureHelpers.js's captureThreePointsWithPreview itself builds
	// on, just for a single point here).
	const centerMarker = createMarker(CURSOR_MARKER_COLOR);
	dm.scene.add(centerMarker);

	dm._showInstruction(tool.name, tool.instructions.center);
	captureFixedPoints(dm, tool, 1, () => tool.instructions.center, (p) => {
		centerMarker.position.copy(p);
		centerMarker.visible = true;
	}).then((points) => {
		dm.scene.remove(centerMarker);
		if(!points){ dm.stopInsertion(); return; }
		runCircleRadiusPhase(dm, tool, points[0]);
	});
}

// Phase 2: dynamic radius (mouse distance to centre, measured IN the
// currently estimated plane) + continuous point-cloud plane estimation
// (tools/livePlaneEstimator.js) - a second click commits. The centre
// itself is re-projected onto the plane on every redraw (never stored as
// "corrected" separately - see geometry/localPlaneFit.js's projectOntoPlane),
// so the operator's own click stays the authoritative in-plane position;
// only its out-of-plane component is ever adjusted, and only by the plane
// the point cloud actually supports.
function runCircleRadiusPhase(dm, tool, centerRawVec){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;
	const centerRaw = toPlainPoint(centerRawVec);

	dm._showInstruction(tool.name, tool.instructions.radius);

	const visual = new FeatureVisual("Polygon");
	visual.setInfo(tool.name, "");
	if(tool.fillColor) visual.enableFill(tool.fillColor, tool.fillOpacity);
	dm.scene.add(visual);

	// User-requested result label (task: "Tilføj en resultat label for
	// Cirkel-værktøjet"), registered up front so it live-updates through
	// the whole dynamic drag AND the post-capture dialog alike (task §7:
	// "Result labels... skal fortsat opdateres fra den aktuelle preview-
	// geometri") - see FeatureVisual.js's enableSummaryLabel()/_sync() doc
	// comments for why one registration is enough. Derived purely from
	// `raw` (the sampled periphery points): sampleCirclePolygon() places
	// points at equal angular steps around the FULL circle, so their
	// average is exactly the centre (the equally spaced unit vectors
	// cancel out) - no need to carry the centre through a separate closure.
	enableCircleResultLabel(visual);

	const readout = createStyledLabel("");
	readout.visible = false;
	dm.scene.add(readout);

	const estimator = createLivePlaneEstimator(dm);

	let lastRadiusM = 0;
	let lastPlane = estimator.currentPlane(centerRaw, WORLD_UP);
	let finished = false;

	const updateScales = () => {
		if(!readout.visible) return;
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const s = computeScreenSpaceScale(camera, readout.getWorldPosition(new THREE.Vector3()), camera.position, size.width, size.height, ScreenSpaceSizes.label);
		readout.scale.set(s, s, s);
	};
	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const redraw = (radiusM, plane) => {
		if(radiusM < 1e-4) return;
		const finalCenter = projectOntoPlane(centerRaw, plane);
		const sample = sampleCirclePolygon(finalCenter, radiusM, {u: plane.uAxis, v: plane.vAxis}, CURVE_SAMPLING_TOLERANCE_M);
		visual.setPoints(toVector3Array(sample.points));

		readout.visible = true;
		readout.setText(`Radius: ${formatLength(radiusM)}`);
		readout.position.set(finalCenter.x, finalCenter.y, finalCenter.z);

		lastRadiusM = radiusM;
		lastPlane = plane;
	};

	const cleanup = () => {
		domElement.removeEventListener("mousemove", onMouseMove, false);
		domElement.removeEventListener("mouseup", onMouseUp, false);
		viewer.removeEventListener("update", onUpdate);
		viewer.removeEventListener("cancel_insertions", onCancel);
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked) return;

		const plane = estimator.currentPlane(centerRaw, WORLD_UP);
		const finalCenter = projectOntoPlane(centerRaw, plane);
		const pickedProjected = projectOntoPlane(toPlainPoint(picked), plane);
		const radiusM = Math.hypot(pickedProjected.x - finalCenter.x, pickedProjected.y - finalCenter.y, pickedProjected.z - finalCenter.z);
		redraw(radiusM, plane);

		estimator.poke(centerRawVec, Math.max(radiusM, 0.1), centerRaw, WORLD_UP, (fit) => {
			if(finished) return;
			redraw(lastRadiusM, fit);
		});
	};

	const finalize = () => {
		finished = true;
		cleanup();
		dm.scene.remove(readout);
		estimator.dispose();

		const finalCenter = projectOntoPlane(centerRaw, lastPlane);
		const radiusM = lastRadiusM;
		const u = lastPlane.uAxis, v = lastPlane.vAxis;

		const feature = createFeature({
			geometryType: "Polygon",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});
		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.registerNewFeature(feature, visual);
		dm.featureManager.setDomainData(feature.id, "circle", buildCircleShape({center: finalCenter, radiusM, u, v}));

		openCircleDialog(dm, visual, {center: finalCenter, u, v}, radiusM, {isNewFeature: true}).then((committed) => {
			if(!committed) dm.removeFeature(feature.id); // task: "Cancel efterlader ingen permanent feature"
			dm.stopInsertion();
		});
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
		if(e.button !== THREE.MOUSE.LEFT) return;
		if(lastRadiusM < 1e-4) return; // no valid preview yet - nothing to commit
		finalize();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		dm.scene.remove(visual);
		dm.scene.remove(readout);
		estimator.dispose();
	};

	domElement.addEventListener("mousemove", onMouseMove, false);
	domElement.addEventListener("mouseup", onMouseUp, false);
	viewer.addEventListener("cancel_insertions", onCancel);
}

// "Rediger cirkel…" (task DEL B) - bound from VectorEditingMenu.js's whole-
// object menu when the selected Polygon carries feature.domainData.circle.
export async function runEditCircle(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature || !feature.domainData.circle) return;
	const shape = feature.domainData.circle;
	await openCircleDialog(dm, visual, planeFromShape(shape), shape.radiusM, {isNewFeature: false});
}

// "Opløs cirkel" (task DEL B) - removes only the parametric definition; the
// current coordinates are left exactly as they are.
export function runDissolveCircle(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature) return;
	dm.featureManager.clearDomainData(feature.id, "circle");
}
